// BlastSeat.jsx
//
// Blast-owned opponent seat — forked from the shared
// components/tables/shared/Seat/Seat.jsx, which is also used by
// PreflopTable/cash-game screens and carries a bunch of cash-game-only
// branching (gameConfig.betType === 'OPEN' / 'FACING_3BET_CALL' / etc.
// overriding the displayed bet size). None of that ever fires for Blast —
// Blast configs use quizType/format, never betType — so it was dead weight
// here, and it's exactly the kind of coupling that would make the eventual
// simulated multi-way hand (fold/elimination state, multiple independently-
// animating villains) harder to build on top of a component two different
// game modes depend on.
//
// Deliberately reuses the exact same class names (seat / seat-on / title /
// big-blinds / preflop-bet-container / cards-container / dealer-button) so
// all existing CSS — BlastTable.css's .blast-table .seat overrides plus
// seat.css's base rules — applies unchanged. Visual output is identical to
// today's Seat; only the cash-game bet-size logic was removed.
import React from 'react';
import '../../shared/Seat/seat.css';

const BlastSeat = ({
  title,
  blinds,
  className,
  isActive,
  // Whether this seat is dealt into the hand at all — separate from
  // isActive (whose turn it is). A seated opponent still holds cards while
  // waiting for their turn; only isActive should change. Defaults to true
  // since every occupied Blast seat is dealt in for the whole hand (no
  // mid-hand folding modeled yet).
  hasCards = true,
  // True once this seat's bet represents a raise rather than the posted
  // blind — swaps to the bigger chips-raise.svg (3-chip stack) instead of
  // the single chip.svg, so a raise reads as more visually significant than
  // a blind post. Chosen by the caller (compares current bet to the
  // starting blind), not derived here — this component doesn't know what
  // the "before" value was.
  isRaise = false,
  isDealer,
  bet,
  potSize,
  showPotWon,
  onPotWonChange,
}) => {
  React.useEffect(() => {
    if (onPotWonChange) onPotWonChange(showPotWon);
  }, [showPotWon, onPotWonChange]);

  return (
    // seat-on means "dealt into the hand" (blue fill, cards visible) — that's
    // what it always meant in the underlying CSS (.seat.seat-on controls
    // background AND .cards-container visibility together); it was just
    // being driven by isActive before, which conflated "seated" with "their
    // turn." seat-turn is the new, separate modifier for whose turn it is —
    // just a border-color override layered on top.
    <div className={`seat ${className} ${hasCards ? 'seat-on' : ''} ${isActive ? 'seat-turn' : ''}`}>
      <div className="seat-content">
        <div className="title">{title}</div>
        <div className="big-blinds">{blinds}</div>
      </div>

      {isDealer && (
        <div className="dealer-button">
          <img src="/svgs/dealer.svg" alt="Dealer" width="24" height="24" />
        </div>
      )}

      {hasCards && (
        <div className="cards-container">
          <div className="card card-1"></div>
          <div className="card card-2"></div>
        </div>
      )}

      {bet > 0 && (
        <div className="preflop-bet-container">
          {/* Order swapped from the original (text, then chip) to (chip,
              then text) — .bet-stack uses flex-direction: column-reverse,
              which places the LAST DOM child nearest the container's
              anchored top edge. chips-raise.svg (34x32) is bigger than
              chip.svg (23x25); with text last/nearest-anchor, the text's
              position is pinned regardless of which chip asset is showing —
              only the chip's own footprint grows/shrinks below it. With the
              old order (text first), the chip sat at the anchor instead and
              growing it pushed the text down every time a raise happened. */}
          <div className="bet-stack">
            <div className="bet-image">
              {/* seat.css hardcodes `.bet-chip { width: 24px; height: 24px; }`,
                  which beats plain width/height HTML attributes regardless of
                  what's passed here — that's why the raise chip was rendering
                  at the same clamped 24x24 box instead of larger. Inline
                  style wins over an external class rule, so use that instead. */}
              <img
                src={isRaise ? '/svgs/chips-raise.svg' : '/svgs/chip.svg'}
                alt="Betting chip"
                className="bet-chip"
                style={isRaise ? { width: 32, height: 30 } : { width: 24, height: 24 }}
              />
            </div>
            <div className="bet-text">{bet}<span className="bet-unit">bb</span></div>
          </div>
        </div>
      )}

      {showPotWon && (
        <div className="pot-won-container">
          <img src="/svgs/pot-won.svg" alt="Pot Won Icon" className="pot-won-icon" width="57" height="42" />
          <span className="pot-won-amount">
            <span className="pot-won-value">{potSize}</span>
            <span className="pot-won-unit">bb</span>
          </span>
        </div>
      )}
    </div>
  );
};

export default React.memo(BlastSeat);
